import Button from '@/common/components/Button'
import IconButton from '@/common/components/IconButton'
import { Notification } from '@/common/components/NotificationPopover/Notification'
import Spinner from '@/common/components/Spinner'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/components/ui/popover'
import { useIntersectionObserver } from '@/common/hooks/useIntersectionObserver'
import { useMarkAllNotificationsAsRead } from '@/common/hooks/useMarkAllNotificationsAsRead'
import { useNotification } from '@/common/hooks/useNotification'
import { useNotificationsRecipientMessages } from '@/common/hooks/useNotificationsRecipientMessages'
import { useNotificationVisibilityDebounce } from '@/common/hooks/useNotificationVisibilityDebounce'
import { useNotificationStore } from '@/common/stores/useNotificationStore'
import { NotificationData } from '@/common/types/notifications'
import { cn } from '@/common/utils/cn'
import { useQueryClient } from '@tanstack/react-query'
import { Bell, CornerUpLeft } from 'lucide-react'
import { useEffect, useState } from 'react'

export const NotificationPopover = () => {
  const [filter, setFilter] = useState<'all' | 'unseen'>('unseen')
  useNotification()
  
  const [notificationsToRead, setNotificationsToRead] = useState<string[]>([])
  const [updateButtonIsVisible, setUpdateButtonIsVisible] = useState(false)

  const queryClient = useQueryClient()

  const {
    notSeenCount,
    setNotifications: setNotificationsOnStore,
    refreshTick
  } = useNotificationStore()

  const [open, onOpenChange] = useState(false)

  const {
    data: notifications,
    meta: notificationsMeta,
    isPending,
    error,
    refetch,
    isRefetching,
  } = useNotificationsRecipientMessages<NotificationData[]>({
    params: { pageSize: 100, qOnlyNotSeen: filter === 'unseen' },
    queryKey: 'unreadNotifications'
  })

  const unreadNotificationsLength = notifications?.filter(({seen_at}) => seen_at === null).length

  const returnNewNotificationsAmount = () => {
    if(notificationsToRead) {
      const calculatedNewNotificationsAmount = notSeenCount + notificationsToRead.length - notificationsMeta.not_seen_count
      if(calculatedNewNotificationsAmount > 0) return calculatedNewNotificationsAmount
    }
    return 0
  }

  const { addVisibleNotification } = useNotificationVisibilityDebounce({
    onSuccess: () => {
      if(filter === 'all') {
        queryClient.invalidateQueries({ queryKey: ['notificationsRecipientMessages'] })
        queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] })
      }
    },
    onError: (error) => {
      console.error('Error marking notifications as read:', error)
    },
  })

  const {mutate: markAllAsRead} = useMarkAllNotificationsAsRead({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationsRecipientMessages'] })
      queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] })
    },
    onError: (error) => {
      console.error('Error marking all notifications as read:', error)
    },
  })

  useEffect(() => {
    if(!open || filter === 'all') {
      queryClient.invalidateQueries({ queryKey: ['notificationsRecipientMessages'] })
    }
    setUpdateButtonIsVisible(false)
  }, [open, filter])

  useEffect(() => {
    const metaNotSeenCount = notificationsMeta.not_seen_count
    const notificationsToReadLength = notificationsToRead.length

    if((notSeenCount !== metaNotSeenCount || (notSeenCount === metaNotSeenCount && notificationsToReadLength > 0)) && returnNewNotificationsAmount() > 0) {
      setUpdateButtonIsVisible(true)
    }
  }, [refreshTick])

  useEffect(() => {
    setNotificationsToRead([])
  }, [isRefetching, open, filter])

  const handleVisibilityChange = (id: string, isVisible: boolean) => {
    if (!isVisible) return;

    addVisibleNotification(id)

    if(filter === 'unseen') {
      setTimeout(() => {
        setNotificationsToRead(prev => {
          if (prev.includes(id)) return prev;
          return [...prev, id];
        });
      }, 1000)
    }
  }

  const { observeElement, unobserveElement } = useIntersectionObserver({
    onVisibilityChange: handleVisibilityChange,
    threshold: 0.5,
    rootMargin: '0px',
  })

  const handleNotificationVisibility = (id: string, element: Element) => {
    observeElement(element, id)

    return () => unobserveElement(id)
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger>
         <div className='flex justify-center w-[82px]'>
            <IconButton
              className={cn('rounded-lg transition-all', {
                'text-black-800 bg-[#FCB34A] hover:bg-[#FCB34A] px-2 w-auto': notSeenCount > 0,
              })}
              Icon={
                <div className="flex items-center gap-x-1">
                  <Bell strokeWidth={1} />
                  {notSeenCount > 0 && (
                    <span className="text-sm">{notSeenCount > 999 ? "999+" : notSeenCount}</span>
                  )}
                </div>
              }
              onClick={() => refetch()}
            />
          </div>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        alignOffset={41}
        className="w-[28rem] bg-[#2C2C2C] text-white-800 p-0 rounded-lg border-none shadow-[0px_4px_4px_0px_rgba(0,_0,_0,_0.251)] overflow-hidden"
      >
        <div className="p-4 flex justify-center items-center gap-x-2 border-b border-[#444444]">
          <IconButton
            onClick={() => onOpenChange(false)}
            Icon={<CornerUpLeft strokeWidth={1} />}
            className="absolute left-2"
          />

          <p className="text-sm">Notificações</p>
        </div>
        <div className="p-4 flex justify-between items-center gap-x-2 border-b border-[#444444]">
          <div className='flex gap-x-2'>
            <Button
              variant={filter === 'all' ? 'neutral' : 'link-white'}
              className='text-xs px-2'
              onClick={() => setFilter('all')}
            >
              Todas
            </Button>
            <Button
              variant={filter === 'unseen' ? 'neutral' : 'link-white'}
              className='text-xs px-2'
              onClick={() => setFilter('unseen')}
            >
              Não lidas ({notSeenCount > 0 ? notSeenCount : 0})
            </Button>
          </div>
          <Button
            variant='link'
            className='justify-self-end'
            onClick={() => {
              markAllAsRead()
            }}
            disabled={unreadNotificationsLength < 1}
          >
            Ler todas
          </Button>
        </div>
        {(notSeenCount > 0 && updateButtonIsVisible && returnNewNotificationsAmount() > 0) && (
          <div className="p-4 flex justify-between items-center gap-x-2 border-b border-[#444444]">
            <div className='flex justify-center items-center w-full gap-x-4'>
              <Button
                onClick={() => {
                  refetch()
                  setUpdateButtonIsVisible(false)
                  queryClient.invalidateQueries({ queryKey: ['notificationsRecipientMessages'] })
                  queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] })
                }}
                variant="link"
              >
                {(() => {
                  const count = returnNewNotificationsAmount()
                  const plural = count > 1
                    return (
                      <p className="text-xs">
                        {count} nova{plural && 's'} notificaç{plural ? 'ões' : 'ão'}. Clique para atualizar
                      </p>
                    )
                })()}
              </Button>
            </div>
          </div>
        )}
        <div>
          {(isPending || (isRefetching && filter === 'unseen')) ? (
            <div className="min-h-[50dvh] p-4 flex flex-col justify-center items-center gap-y-4">
              <Spinner className="size-20 fill-white-800" />
              <p className="text-sm font-bold">Carregando...</p>
            </div>
          ) : !!error ? (
            <div className="min-h-[50dvh] flex flex-col justify-center items-center">
              <img src="/cloud.gif" className="w-full shrink-0 -mt-8" />
              <div className="flex flex-col items-center gap-y-4 -mt-16 p-8">
                <div className="space-y-2">
                  <p className="text-sm font-bold text-center">Ops, algo deu errado!</p>
                  <p className="text-xs text-center">Não conseguimos carregar os dados. Por favor, tente novamente.</p>
                </div>
                <Button
                  onClick={() => {
                    refetch()
                    setNotificationsOnStore([])
                  }}
                >
                  Tentar novamente
                </Button>
              </div>
            </div>
          ) : notifications?.length ? (
            <div className="max-h-[50dvh] overflow-y-auto">
              {notifications?.map(({ id, content, created_at, seen_at }) => (
                <Notification
                  key={id}
                  id={id}
                  content={content}
                  date={new Date(created_at)}
                  seenAt={seen_at}
                  onVisibilityChange={handleNotificationVisibility}
                  notificationsToRead={notificationsToRead}
                />
              ))}
            </div>
          ) : (
            <div className="min-h-[50dvh] flex flex-col justify-center items-center">
              {!isRefetching && open ? (
                <div className='overflow-hidden'>
                  <img src="/search.gif" className="w-full shrink-0 -mt-8" />
                  <div className="-mt-16 mb-16 p-8 space-y-2">
                    <p className="text-sm font-bold text-center">Você não tem novas notificações.</p>
                    <p className="text-xs text-center">Fique tranquilo, qualquer novidade aparecerá por aqui.</p>
                  </div>
                </div>
              ) : (
                (isRefetching && open) && (
                  <div className="min-h-[50dvh] p-4 flex flex-col justify-center items-center gap-y-4">
                    <Spinner className="size-20 fill-white-800" />
                    <p className="text-sm font-bold">Carregando...</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
